/**
 * Tile trend disclosure (UX-007 §9) — a row of KPI tiles where any tile that has
 * a series shows a caret; clicking it outlines the tile and opens a trend panel
 * directly UNDER THE ROW (TimeSeriesTile at 120px + Open-in-explorer + Close).
 * One tile open at a time per row. Built on @solarkey/components (.tiles/.tile/
 * .caret/.trend) + the UX-006 5.1 TimeSeriesTile. Reusable on every screen.
 */
import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { useGetV1Series } from "@solarkey/api-client";
import { TimeSeriesTile } from "@solarkey/charts";

export interface TrendSeriesRef {
  site: string;
  signal: string;
  from: string;
  to: string;
  unit?: string;
  label?: string;
}

export interface TrendTileDef {
  key: string;
  label: ReactNode;
  /** Plain-text label for the trend panel heading + chart (uPlot/aria need a
   *  string). Defaults to `label` when that is a string; required when `label`
   *  is a rich node (e.g. a <Term>) and the tile has a `series`. */
  labelText?: string;
  value: ReactNode;
  sub?: ReactNode;
  /** When present, the tile gets a caret and opens its trend. */
  series?: TrendSeriesRef;
  wide?: boolean;
}

export function TrendTileRow(props: {
  tiles: TrendTileDef[];
  /** Grid template columns; defaults to one equal column per tile. */
  columns?: string;
  /** tenant/scope for the "Open in explorer" link, if available. */
  explorer?: { tenant: string; scope: string };
}): ReactNode {
  const [open, setOpen] = useState<string | null>(null);
  const openDef = props.tiles.find((t) => t.key === open && t.series);
  const cols = props.columns ?? `repeat(${props.tiles.length}, minmax(0, 1fr))`;

  return (
    <div>
      <div className="tiles" style={{ gridTemplateColumns: cols }}>
        {props.tiles.map((t) => {
          const isOpen = open === t.key;
          return (
            <div key={t.key} className={`tile${isOpen ? " open" : ""}`}>
              {t.series ? (
                <button
                  type="button"
                  className={`caret${isOpen ? " on" : ""}`}
                  aria-label={isOpen ? "Hide trend" : "Show trend"}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : t.key)}
                >
                  ▾
                </button>
              ) : null}
              <span className="lab">{t.label}</span>
              <span className="val">{t.value}</span>
              {t.sub ? <span className="sub">{t.sub}</span> : null}
            </div>
          );
        })}
      </div>
      {openDef?.series ? (
        <div style={{ marginTop: 10 }}>
          <TrendPanel
            title={openDef.labelText ?? (typeof openDef.label === "string" ? openDef.label : "")}
            series={openDef.series}
            onClose={() => setOpen(null)}
            explorer={props.explorer}
          />
        </div>
      ) : null}
    </div>
  );
}

/** A small caret button for a tile that has a series (UX-007 §9). The tile it
 *  sits in must be position:relative. */
export function TrendCaret(props: { open: boolean; onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      className={`caret${props.open ? " on" : ""}`}
      aria-label={props.open ? "Hide trend" : "Show trend"}
      aria-expanded={props.open}
      onClick={props.onClick}
    >
      ▾
    </button>
  );
}

/** The trend panel (UX-007 §9) — accent-bordered, the series at 120px with
 *  Open-in-explorer + Close. Opens directly under the tile row. */
export function TrendPanel(props: {
  title: string;
  series: TrendSeriesRef;
  onClose: () => void;
  explorer?: { tenant: string; scope: string };
}): ReactNode {
  const s = props.series;
  const q = useGetV1Series({ site: s.site, signal: s.signal, from: s.from, to: s.to });
  const ok = q.data?.status === 200 ? q.data.data.data : undefined;
  const points = ok?.points ?? [];
  const times = points.map((p) => Date.parse(p.ts) / 1000);
  const values = points.map((p) => (typeof p.value === "number" ? p.value : null));
  const unit = s.unit ?? ok?.unit ?? "";

  // Clamp the view to generation hours 06:00–19:00 of the data's day (UX-006 §2);
  // horizontal-only gridlines come from TimeSeriesTile itself.
  const dayMs = times.length > 0 ? times[0]! * 1000 : Date.parse(s.from);
  const d6 = new Date(dayMs); d6.setHours(6, 0, 0, 0);
  const d19 = new Date(dayMs); d19.setHours(19, 0, 0, 0);
  const xRange: [number, number] = [d6.getTime() / 1000, d19.getTime() / 1000];

  return (
    <section className="trend">
      <h5>
        {props.title}
        {s.label ? <span className="sub">{s.label}</span> : null}
        <span className="right">
          {props.explorer ? (
            <Link
              to="/t/$tenant/s/$scope/plants/$site/explorer"
              params={{ tenant: props.explorer.tenant, scope: props.explorer.scope, site: s.site }}
              className="btn small"
            >
              Open in explorer
            </Link>
          ) : null}
          <button type="button" className="btn small" onClick={props.onClose}>
            Close
          </button>
        </span>
      </h5>
      {q.isLoading ? (
        <div style={{ height: 220, display: "grid", placeItems: "center", color: "var(--sk-color-ink-3)", fontSize: 11 }}>
          Loading trend…
        </div>
      ) : times.length === 0 ? (
        <div style={{ height: 220, display: "grid", placeItems: "center", color: "var(--sk-color-ink-3)", fontSize: 11 }}>
          No series for this figure
        </div>
      ) : (
        <TimeSeriesTile label={props.title} unit={unit} times={times} values={values} xRange={xRange} height={220} />
      )}
    </section>
  );
}
